import React from 'react';
import PropTypes from 'prop-types';
import { WeezHeader, Button, Icon, ContentCard, Paginator, Card, FilterSetController, Filter, Modal, EmptyState, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import ProductModel from '../../../../models/productModel';
import Style from './crossSelling.module.css';
import LoadingCard from '../../../../components/loadingCard';
import CrossSellingItem from './crossSellingItem';
import DuplicateModal from './duplicateModal';
import CreateProductModal from '../../../product/list/components/createProductModal';

export default function CrossSellingList({ organization }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const optionsVisibility = [
        {
            key: 'visible',
            text: t('ticket.cross-selling.visible'),
            name: 'True'
        },
        {
            key: 'not_visible',
            text: t('ticket.cross-selling.notVisible'),
            name: 'False'
        }
    ];

    const optionsCategory = [
        {
            key: 'categoryServices',
            text: t('ticket.cross-selling.cashless'),
            name: 1
        },
        {
            key: 'categoryServices',
            text: t('ticket.cross-selling.service'),
            name: 2
        },
        {
            key: 'categoryProduct',
            text: t('ticket.cross-selling.product'),
            name: 3
        }
    ];

    const optionsCible = [
        {
            key: 'categoryParticipant',
            text: t('ticket.cross-selling.attendee'),
            name: 'False'
        },
        {
            key: 'categoryBuyer',
            text: t('ticket.cross-selling.buyer'),
            name: 'True'
        }
    ];

    const [filters, setFilters] = React.useState({});
    const [openDeleteModal, setOpenDeleteModal] = React.useState(false);
    const [openDuplicateModal, setOpenDuplicateModal] = React.useState(false);
    const [openCreateModal, setOpenCreateModal] = React.useState(false);
    const [selectedProduct, setSelectedProduct] = React.useState(null);
    const [inputSearchValue, setInputSearchValue] = React.useState('');
    const [currentPage, setCurrentPage] = React.useState(0);

    const [paginationProducts, setPaginationProducts] = React.useState({
        limit: 20,
        offset: 0
    });

    const getURL = React.useCallback(() => {
        let param_object = {};

        param_object = handleSearchQuery(inputSearchValue, filters);
        param_object['ordering'] = 'start_date';
        param_object['expand'] = 'variants';
        if (inputSearchValue) {
            param_object['search'] = inputSearchValue;
        }

        param_object['limit'] = paginationProducts.limit;
        param_object['offset'] = paginationProducts.offset;
        return param_object;
    }, [filters, inputSearchValue, paginationProducts]);

    let handleSearchQuery = (value, filters) => {
        let query = {};
        if (value !== '') {
            query.search = value;
        }

        if (filters.visibilityFilter) {
            query.disabled = filters.visibilityFilter !== 'True';
        }
        if (filters.categoryFilter) {
            query.category = filters.categoryFilter;
        }
        if (filters.ciblesFilter) {
            query.for_buyer = filters.ciblesFilter === 'True';
        }

        return query;
    };

    const [_products, loadingProducts, sync] = ProductModel.useApiModel(
        {
            organizationId: organization.id,
            cache: false,
            query: getURL()
        },
        [organization, getURL]
    );

    let resetPagination = React.useCallback(() => {
        setPaginationProducts({
            limit: 20,
            offset: 0
        });
    }, [setPaginationProducts]);

    let handleSearch = React.useCallback(
        (synth, value) => {
            setInputSearchValue(value);
            resetPagination();
        },
        [resetPagination]
    );

    const updateFilters = React.useCallback(
        (name, value) => {
            setFilters(filters => {
                let previousFilters = { ...filters };
                //Reset filters
                if (name === 'reset') {
                    resetPagination();
                    return {};
                }
                if (!value) {
                    delete previousFilters[name];
                    resetPagination();
                    return previousFilters;
                }
                let newFilters = {
                    ...previousFilters,
                    [name]: value
                };
                resetPagination();
                return newFilters;
            });
        },
        [resetPagination]
    );

    // edit product
    const onEdit = React.useCallback(
        (value, productId) => {
            new ProductModel({ id: productId, ...value })
                .save({
                    organizationId: organization.id
                })
                .then(() => {
                    toast.success(t('ticket.cross-selling.productEdited'));
                    sync();
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t('ticket.cross-selling.productEditError'));
                });
        },
        [toast, _products, sync]
    );

    // delete product
    const onDelete = React.useCallback(() => {
        new ProductModel({ id: selectedProduct.id })
            .delete({ organizationId: organization.id })
            .then(() => {
                toast.success(t('ticket.cross-selling.add.productDeleted'));
                sync();
            })
            .catch(e => {
                toast.error(t('ticket.cross-selling.add.productDeleteError'));
                console.error(e);
            })
            .finally(() => {
                setOpenDeleteModal(false);
            });
    }, [organization, selectedProduct, toast, sync]);

    const countProducts = React.useMemo(() => {
        return Number.parseInt(_products?.__meta?.headers.get('total-count')) || 0;
    }, [_products]);

    const handlePage = React.useCallback(
        value => {
            setCurrentPage(value);

            setPaginationProducts({
                limit: paginationProducts.limit,
                offset: paginationProducts.limit * value
            });
        },
        [paginationProducts]
    );

    return (
        <>
            {/* create modal */}
            <CreateProductModal organization={organization} openModal={openCreateModal} setOpenModal={setOpenCreateModal} sync={sync} />

            {/* delete modal */}
            <Modal
                size="medium"
                open={openDeleteModal}
                allowExternalClosing={false}
                onClose={() => setOpenDeleteModal(false)}
                title={t('ticket.cross-selling.confirmDelete')}
                submitButton={
                    <Button
                        primary
                        onClick={() => {
                            onDelete();
                        }}
                        label={t('ticket.common.action.confirm')}
                    />
                }
                cancelButton={<Button onClick={() => setOpenDeleteModal(false)} label={t('ticket.common.action.cancel')} />}
            >
                <div>{t('ticket.cross-selling.confirmDeleteProductQuestion')}</div>
            </Modal>

            {/* duplicate modal */}
            <DuplicateModal
                organization={organization}
                selectedProduct={selectedProduct}
                openDuplicateModal={openDuplicateModal}
                setOpenDuplicateModal={setOpenDuplicateModal}
                sync={sync}
            />

            <WeezHeader
                title={t('ticket.cross-selling.title')}
                rightComponent={[
                    <Button
                        primary
                        key="rate"
                        onClick={() => {
                            setOpenCreateModal(true);
                        }}
                        label={t('ticket.cross-selling.addObject')}
                        icon={<Icon name="plus" margin={false} />}
                    />
                ]}
            >
                {t('ticket.cross-selling.sub')}
            </WeezHeader>

            <Card className={Style['cross-selling-filter']}>
                <ContentCard>
                    <FilterSetController
                        total={_products?.length}
                        isLoadingTotal={loadingProducts}
                        labelTotal={t('ticket.cross-selling.labelTotal', { count: _products?.length })}
                        labelReset={t('ticket.common.action.reset')}
                        filterLabel={t('ticket.cross-selling.lebelFilters')}
                        initialValues={filters}
                        onChange={values => {
                            setFilters(values);
                        }}
                        search={{
                            onChange: (synth, value) => {
                                handleSearch(synth, value);
                            },
                            handleReset: () => {
                                handleSearch('');
                            },
                            displayResetIcon: true,
                            placeholder: t('formEvents.search'),
                            value: inputSearchValue
                        }}
                        onReset={() => updateFilters('reset')}
                    >
                        <Filter name="visibilityFilter" labelName={t('ticket.cross-selling.visibility')} options={optionsVisibility} index="1" />
                        <Filter name="categoryFilter" labelName={t('ticket.cross-selling.category')} options={optionsCategory} index="2" />
                        <Filter name="ciblesFilter" labelName={t('ticket.cross-selling.target')} options={optionsCible} index="3" />
                    </FilterSetController>
                </ContentCard>
            </Card>

            <LoadingCard loading={loadingProducts} height="50vh">
                {_products?.length > 0 ? (
                    <div className={Style['line-table-cross-selling']}>
                        {_products.map((product, index) => (
                            <CrossSellingItem
                                key={index}
                                index={index}
                                product={product}
                                products={_products}
                                onEdit={onEdit}
                                onOpenDeleteModal={setOpenDeleteModal}
                                onOpenDuplicateModal={setOpenDuplicateModal}
                                organization={organization}
                                onSelectProduct={setSelectedProduct}
                            />
                        ))}
                        <div className={Style['contentPaginator']}>
                            <Paginator currentPage={currentPage} onPageChange={handlePage} totalCount={countProducts} pageSize={paginationProducts.limit} />
                        </div>
                    </div>
                ) : (
                    <Card>
                        <Card.Content>
                            <EmptyState
                                icon={<Icon name="list" />}
                                itle={t('ticket.cross-selling.empty.title')}
                                subtitle={t('ticket.cross-selling.empty.sub')}
                                labelButton={t('ticket.cross-selling.empty.labelButton')}
                                onClick={() => setOpenCreateModal(true)}
                            />
                        </Card.Content>
                    </Card>
                )}
            </LoadingCard>
        </>
    );
}

CrossSellingList.propTypes = {
    organization: PropTypes.object.isRequired
};
